import { useEffect, useRef, useState, useCallback } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

const PAGES = Array.from({ length: 9 }, (_, i) => `/assets/deck/p${String(i).padStart(2, "0")}.jpg`);
const TRACK = [...PAGES, ...PAGES];

function DeckLightbox({ open, setOpen, step, closeFromBackdrop }) {
  return (
    <div
      data-testid="deck-lightbox"
      className="fixed inset-0 z-50 bg-ink/85 flex items-center justify-center p-4 md:p-10"
      onClick={closeFromBackdrop}
    >
      <button
        data-testid="deck-lightbox-close"
        aria-label="Close"
        onClick={() => setOpen(null)}
        className="absolute top-4 right-4 text-paper hover:text-yellow transition-colors"
      >
        <X size={32} />
      </button>
      <button
        data-testid="deck-lightbox-prev"
        aria-label="Previous page"
        onClick={(e) => { e.stopPropagation(); step(-1); }}
        className="absolute left-2 md:left-6 text-paper hover:text-yellow transition-colors"
      >
        <ChevronLeft size={44} />
      </button>
      <img
        src={PAGES[open].replace("/deck/", "/deck/full/")}
        alt={`RUNRS deck page ${open + 1}`}
        data-testid="deck-lightbox-image"
        onClick={(e) => e.stopPropagation()}
        className="max-h-full max-w-full border-2 border-paper bg-white"
      />
      <button
        data-testid="deck-lightbox-next"
        aria-label="Next page"
        onClick={(e) => { e.stopPropagation(); step(1); }}
        className="absolute right-2 md:right-6 text-paper hover:text-yellow transition-colors"
      >
        <ChevronRight size={44} />
      </button>
      <p className="absolute bottom-4 left-1/2 -translate-x-1/2 font-college text-paper text-sm" data-testid="deck-lightbox-counter">
        {open + 1} / {PAGES.length}
      </p>
    </div>
  );
}

function useDeckInteraction(ref, open, setOpen) {
  const paused = useRef(false);
  const drag = useRef({ down: false, x: 0, left: 0, moved: 0 });
  const openedAt = useRef(0);

  useEffect(() => {
    const el = ref.current;
    let raf;
    const tick = () => {
      if (el && !paused.current && !drag.current.down && open === null) {
        el.scrollLeft += 0.7;
        if (el.scrollLeft >= el.scrollWidth / 2) el.scrollLeft -= el.scrollWidth / 2;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [ref, open]);

  const onDown = (e) => {
    drag.current = { down: true, x: e.clientX, left: ref.current.scrollLeft, moved: 0 };
    ref.current.setPointerCapture(e.pointerId);
  };
  const onMove = (e) => {
    if (!drag.current.down) return;
    const dx = e.clientX - drag.current.x;
    drag.current.moved = Math.max(drag.current.moved, Math.abs(dx));
    ref.current.scrollLeft = drag.current.left - dx;
  };
  const onUp = (e) => {
    const wasDrag = drag.current.moved >= 6;
    drag.current.down = false;
    if (!wasDrag && e.clientX !== undefined) {
      const el = document.elementFromPoint(e.clientX, e.clientY);
      const idx = el?.dataset?.deckIndex;
      if (idx !== undefined) {
        openedAt.current = Date.now();
        setOpen(Number(idx));
      }
    }
  };
  const onPageClick = (i) => {
    if (drag.current.moved < 6) {
      openedAt.current = Date.now();
      setOpen((o) => (o === null ? i % PAGES.length : o));
    }
  };
  const closeFromBackdrop = () => {
    if (Date.now() - openedAt.current < 450) return;
    setOpen(null);
  };
  const setPaused = (v) => (paused.current = v);

  return { onDown, onMove, onUp, onPageClick, closeFromBackdrop, setPaused };
}

export function DeckScroller() {
  const ref = useRef(null);
  const [open, setOpen] = useState(null);
  const { onDown, onMove, onUp, onPageClick, closeFromBackdrop, setPaused } = useDeckInteraction(ref, open, setOpen);

  const step = useCallback((d) => setOpen((o) => (o + d + PAGES.length) % PAGES.length), []);

  useEffect(() => {
    if (open === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(null);
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, step]);

  return (
    <>
      <div
        ref={ref}
        data-testid="deck-scroller"
        className="w-full overflow-x-auto cursor-grab active:cursor-grabbing select-none [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerCancel={onUp}
      >
        <div className="flex gap-5 md:gap-7 px-5 py-6 w-max items-center">
          {TRACK.map((src, i) => {
            const pageIdx = i % PAGES.length;
            return (
              <img
                key={`${src}-loop${Math.floor(i / PAGES.length)}`}
                src={src}
                alt={`RUNRS deck page ${pageIdx + 1}`}
                draggable={false}
                loading={i < 6 ? "eager" : "lazy"}
                data-testid={`deck-page-${pageIdx}`}
                data-deck-index={pageIdx}
                onClick={() => onPageClick(i)}
                className={`h-56 sm:h-72 md:h-96 w-auto shadow-print-sm bg-white shrink-0 cursor-pointer hover:opacity-90 transition-opacity ${i % 2 ? "rotate-[0.6deg]" : "-rotate-[0.6deg]"}`}
              />
            );
          })}
        </div>
      </div>

      {open !== null && (
        <DeckLightbox open={open} setOpen={setOpen} step={step} closeFromBackdrop={closeFromBackdrop} />
      )}
    </>
  );
}
